/** XeAgents.ai**/
import { Stethoscope, Users, BookOpen, Mic, Link as LinkIcon, Phone, Settings, ChevronsUpDown, BarChart3, Home, Target, Zap, LogOut, Coins, Shield, CreditCard, TrendingUp, UserCheck, Workflow, Webhook, ClipboardList, Calendar, CalendarClock, Layout, FileText, HelpCircle, Mail, Ticket, Package, AlertCircle, Wrench, Box, Truck, DollarSign, Briefcase, Plug, Search, ChevronDown } from "lucide-react";
import { useEffect, useState } from "react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarHeader,
  SidebarFooter,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { useLocation, useRoute, useSearch } from "wouter";
import { TenantLink as Link } from "@/components/TenantLink";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from 'react-i18next';
import { useBranding } from "@/components/BrandingProvider";
import { useTheme } from "@/components/ThemeProvider";
import { useFeatures, useCustomModules, useTenantBranding, useTenant, type CustomModule } from "@/components/TenantProvider";
import { AuthStorage } from "@/lib/auth-storage";
import { getTenantLogo, hasTenantLogo } from "@/components/tenant-logos";
import { withBase } from "@/lib/base-path";

interface User {
  id: string;
  name: string;
  email: string;
  isSuperAdmin: boolean;
  role?: string;
  credits?: number;
  planType?: string;
}

interface OrgResponse {
  organization: { id: string; name: string; [key: string]: unknown };
  role: string | null;
}

// Icon map for custom modules
const ICON_MAP: Record<string, any> = {
  Package, AlertCircle, Wrench, Box, Truck, DollarSign, Users, Target, Phone,
  Workflow, Settings, BarChart3, Home, BookOpen, Mic, Calendar, HelpCircle
};

export function AppSidebar() {
  const [location, setLocation] = useLocation();
  const { t } = useTranslation();
  const { branding, currentLogo } = useBranding();
  const { theme } = useTheme();
  const { setOpenMobile, isMobile, state: sidebarState } = useSidebar();

  // Get tenant features and custom modules
  const features = useFeatures();
  const customModules = useCustomModules();
  const tenantBranding = useTenantBranding();
  const { tenantSlug } = useTenant();

  // Get custom logo component for tenant (if available)
  const tenantLogo = getTenantLogo(tenantSlug);
  const hasCustomLogo = hasTenantLogo(tenantSlug);

  const handleNavClick = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  const topItems = [
    { title: t('nav.home'), url: "/app", icon: Home },
  ];

  // Build items - filter based on features
  const buildItems = [
    features.campaigns && { title: t('nav.campaigns'), url: "/app/campaigns", icon: Target, iconColor: "text-orange-500" },
    features.voiceAgents && { title: t('nav.agents'), url: "/app/agents", icon: Users, iconColor: "text-blue-500" },
    { title: "Healthcare", url: "/app/healthcare", icon: Stethoscope, iconColor: "text-rose-500" },
    { title: t('nav.promptTemplates'), url: "/app/prompt-templates", icon: FileText, iconColor: "text-green-500" },
    features.knowledgeBase && { title: t('nav.knowledgeBase'), url: "/app/knowledge-base", icon: BookOpen, iconColor: "text-violet-500" },
    { title: t('nav.voices'), url: "/app/voices", icon: Mic, iconColor: "text-pink-500" },
  ].filter(Boolean) as Array<{ title: string; url: string; icon: any; iconColor?: string }>;

  // Call Logs is the single entry point for call history. Recordings is the
  // only sub-item: it already carries the transcript and the campaign.
  const callLogItems = [
    { title: t('nav.recordings'), url: "/app/calls?tab=recordings", icon: Mic, iconColor: "text-pink-500" },
    { title: t('nav.leads'), url: "/app/leads", icon: ClipboardList, iconColor: "text-violet-500" },
  ];

  const evaluateItems = [
    { title: t('nav.allContacts'), url: "/app/contacts", icon: UserCheck, iconColor: "text-teal-500" },
    { title: "Follow-up Queue", url: "/app/follow-up-queue", icon: CalendarClock, iconColor: "text-amber-500", highlight: true },
    { title: t('nav.callLogs'), url: "/app/calls", icon: Phone, iconColor: "text-purple-500", children: callLogItems },
    { title: t('nav.analytics'), url: "/app/analytics", icon: BarChart3, iconColor: "text-indigo-500" },
  ].filter(Boolean) as Array<{ title: string; url: string; icon: any; iconColor?: string; highlight?: boolean; children?: Array<{ title: string; url: string; icon: any; iconColor?: string }> }>;

  // The tab lives in the query string, so any entry that differs only by ?tab= has to read it
  const searchString = useSearch();
  const activeTabParam = new URLSearchParams(searchString).get("tab") || "";
  const isCallLogsRoute = location === "/app/calls" || location.startsWith("/app/calls/") || location === "/app/leads";
  const [callLogsOpen, setCallLogsOpen] = useState(isCallLogsRoute);

  // Never leave the sub-menu collapsed while the user is standing inside it
  useEffect(() => {
    if (isCallLogsRoute) setCallLogsOpen(true);
  }, [isCallLogsRoute]);

  const subItemTab = (url: string) => new URLSearchParams(url.split("?")[1] || "").get("tab") || "";
  const subItemPath = (url: string) => url.split("?")[0];

  const organizationItems = [
    { title: "Overview", url: "/app/organization", icon: Home, iconColor: "text-blue-500" },
    { title: "Team Members", url: "/app/organization/members", icon: Users, iconColor: "text-green-500" },
    { title: "Invitations", url: "/app/organization/invitations", icon: Mail, iconColor: "text-amber-500" },
    { title: "Integrations", url: "/app/organization/integrations", icon: Plug, iconColor: "text-cyan-500" },
    { title: "Billing & Plans", url: "/app/organization/billing", icon: CreditCard, iconColor: "text-purple-500" },
    { title: "Upgrade Plan", url: "/app/upgrade", icon: TrendingUp, iconColor: "text-amber-500" },
    { title: "Purchase Credits", url: "/app/billing", icon: Coins, iconColor: "text-green-500" },
    { title: "Transaction History", url: "/app/transaction-history", icon: FileText, iconColor: "text-slate-500" },
  ];

  // Organization group starts collapsed on load, unless the user lands inside the section
  const isOrgRoute = organizationItems.some((item) => location === item.url);
  const [orgMenuOpen, setOrgMenuOpen] = useState(isOrgRoute);

  // Never leave the section collapsed while the user is standing inside it
  useEffect(() => {
    if (isOrgRoute) setOrgMenuOpen(true);
  }, [isOrgRoute]);

  // In icon mode the group label (and so the toggle) is hidden, so keep the icons reachable
  const orgMenuForcedOpen = !isMobile && sidebarState === "collapsed";

  const telephonyItems = [
    features.phoneNumbers && { title: t('nav.phoneNumbers'), url: "/app/phone-numbers", icon: Phone, iconColor: "text-emerald-500" },
    { title: t('nav.incomingConnections'), url: "/app/incoming-connections", icon: LinkIcon, iconColor: "text-amber-500" },
  ].filter(Boolean) as Array<{ title: string; url: string; icon: any; iconColor?: string }>;

  // Flow automation items - only show if flowBuilder feature is enabled
  const flowAutomationItems = features.flowBuilder ? [
    { title: t('nav.flowBuilder'), url: "/app/flows", icon: Workflow, iconColor: "text-indigo-500" },
    { title: t('nav.executionLogs'), url: "/app/flows/execution", icon: BarChart3, iconColor: "text-slate-500" },
    features.webhooks && { title: t('nav.webhooks'), url: "/app/flows/webhooks", icon: Webhook, iconColor: "text-violet-500" },
    { title: t('nav.forms'), url: "/app/flows/forms", icon: ClipboardList, iconColor: "text-cyan-500" },
    features.calendarIntegration && { title: t('nav.appointments'), url: "/app/flows/appointments", icon: Calendar, iconColor: "text-rose-500" },
    { title: t('nav.templates'), url: "/app/flows/templates", icon: Layout, iconColor: "text-fuchsia-500" },
  ].filter(Boolean) as Array<{ title: string; url: string; icon: any; iconColor?: string }> : [];

  // Custom modules from tenant config
  const customModuleItems = customModules.map((module: CustomModule) => ({
    title: module.displayName,
    url: module.route,
    icon: ICON_MAP[module.icon || 'Package'] || Package,
    iconColor: "text-emerald-500",
  }));

  const supportItems = [
    { title: t('nav.helpDesk'), url: "/app/helpdesk", icon: Ticket, iconColor: "text-orange-500" },
    { title: t('nav.help'), url: "/app/help", icon: HelpCircle, iconColor: "text-teal-500" },
  ];

  // Fetch current user data including credits - ONLY from server, no localStorage fallback
  const { data: user, isLoading: userLoading } = useQuery<User>({
    queryKey: ["/api/auth/me"],
    // The balance changes server-side when a call ends; nothing in the browser knows that happened.
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
  });

  const { data: orgData } = useQuery<OrgResponse>({
    queryKey: ["/api/organizations/me"],
    enabled: !!user,
  });

  // Only a super admin can see the AI B2B Leads entry at all, so nobody else needs to ask about it. The sidebar
  // outlives navigation, so this is asked once and then left alone.
  const { data: aiB2bLeadsMenu } = useQuery<{ enabled: boolean }>({
    queryKey: ["/api/prospect-leads/menu"],
    enabled: !!user?.isSuperAdmin,
    staleTime: 5 * 60 * 1000,
  });

  // If no user data from server, don't render (security: prevents localStorage spoofing)
  if (userLoading || !user) {
    return null;
  }

  const userName = user.name || "User";
  const userInitial = userName.charAt(0).toUpperCase() || "U";
  const isPro = user.planType === "pro";

  // Credits from user data
  const remainingCredits = user.credits || 0;

  const canSeeOrgMenu = orgData?.role === "owner" || orgData?.role === "admin";

  // Leads (plain-English lead search with add-to-campaign) is the only lead-search entry now: the old AI B2B Lead and
  // Lead Lists entries are gone from the menu (their routes still work for old bookmarks). Super admin only (v1): the API
  // answers everyone else with 403, and a super admin can switch it off in System Settings > Menu Visibility.
  const leadsSearchItem: (typeof buildItems)[number] = { title: t('nav.leadsSearch', 'Leads'), url: "/app/leads-search", icon: Search, iconColor: "text-violet-600" };
  // Hidden until the answer is in: a section someone switched off must not flash up while the flag is loading.
  const showAiB2bLeads = user.isSuperAdmin && aiB2bLeadsMenu?.enabled === true;
  const visibleBuildItems = (() => {
    if (!showAiB2bLeads) return buildItems;
    const at = buildItems.findIndex((i) => i.url === "/app/campaigns");
    return at === -1 ? [...buildItems, leadsSearchItem] : [...buildItems.slice(0, at + 1), leadsSearchItem, ...buildItems.slice(at + 1)];
  })();

  const handleLogout = () => {
    AuthStorage.clearAuth();
    window.location.href = withBase("/");
  };

  // Determine which logo to use: tenant branding takes priority, otherwise use default logo (same as home page)
  const defaultLogo = theme === "dark" ? "/images/branding/voiceprologowhite.svg" : "/images/branding/voiceprologo.svg";
  const sidebarLogo = tenantBranding.logoUrl || defaultLogo;
  const sidebarLogoAlt = tenantBranding.companyName || branding.app_name || "VoicePro";

  return (
    <Sidebar collapsible="icon" style={{ '--sidebar-width-icon': '3.375rem' } as React.CSSProperties}>
      {/* Header with responsive logo */}
      <SidebarHeader className="px-3 py-0 border-b border-sidebar-border h-14">
        {/* When expanded: Logo on left, toggle on right */}
        <div className="flex items-center justify-between gap-2 h-full group-data-[collapsible=icon]:hidden">
          {hasCustomLogo && tenantLogo ? (
            <div className="ml-2">
              <tenantLogo.Logo size="md" showText={true} />
            </div>
          ) : (
            <img
              src={sidebarLogo}
              alt={sidebarLogoAlt}
              className="h-16 w-auto max-w-[200px] object-contain ml-4"
            />
          )}
          <SidebarTrigger
            className="h-5 w-5 shrink-0"
            data-testid="button-sidebar-toggle"
          />
        </div>
        {/* When collapsed: Favicon + toggle side by side */}
        <div className="hidden group-data-[collapsible=icon]:flex flex-row items-center justify-center gap-2 h-full">
          {hasCustomLogo && tenantLogo ? (
            <tenantLogo.LogoIcon size="sm" />
          ) : (
            <img
              src={tenantBranding.faviconUrl || "/images/branding/Xe-Logo-192.png"}
              alt={sidebarLogoAlt}
              className="h-12 w-12 object-contain"
            />
          )}
          <SidebarTrigger
            className="h-4 w-4 shrink-0"
            data-testid="button-sidebar-toggle-collapsed"
          />
        </div>
      </SidebarHeader>

      <SidebarContent className="group-data-[collapsible=icon]:px-0 px-2 py-1">
        {/* Home */}
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {topItems.map((item) => (
                <SidebarMenuItem key={item.url}>
                  <SidebarMenuButton
                    asChild
                    isActive={location === item.url}
                    tooltip={item.title}
                    data-testid={`link-${item.title.toLowerCase()}`}
                  >
                    <Link href={item.url} onClick={handleNavClick}>
                      <item.icon className="h-4 w-4" />
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Build Section */}
        <SidebarGroup>
          <SidebarGroupLabel className="px-3 text-xs font-bold uppercase tracking-wide text-muted-foreground">{t('sidebar.build')}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {visibleBuildItems.map((item) => (
                <SidebarMenuItem key={item.url}>
                  <SidebarMenuButton
                    asChild
                    isActive={location === subItemPath(item.url) && activeTabParam === subItemTab(item.url)}
                    tooltip={item.title}
                    data-testid={`link-${item.title.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <Link href={item.url} onClick={handleNavClick}>
                      <item.icon className={`h-4 w-4 ${item.iconColor || ''}`} />
                      <span className="truncate">{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Evaluate Section */}
        <SidebarGroup>
          <SidebarGroupLabel className="px-3 text-xs font-bold uppercase tracking-wide text-muted-foreground">{t('sidebar.evaluate')}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {evaluateItems.map((item) => (
                item.children ? (
                  <Collapsible
                    key={item.url}
                    open={callLogsOpen}
                    onOpenChange={setCallLogsOpen}
                    className="group/call-logs"
                  >
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        asChild
                        isActive={location === item.url && activeTabParam === ""}
                        tooltip={item.title}
                        data-testid={`link-${item.title.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        <Link href={item.url} onClick={handleNavClick}>
                          <item.icon className={`h-4 w-4 ${item.iconColor || ''}`} />
                          <span>{item.title}</span>
                        </Link>
                      </SidebarMenuButton>
                      <CollapsibleTrigger asChild>
                        <SidebarMenuAction
                          className="group-data-[collapsible=icon]:hidden"
                          data-testid="button-toggle-call-logs"
                        >
                          <ChevronDown className="h-3.5 w-3.5 shrink-0 transition-transform duration-200 group-data-[state=closed]/call-logs:-rotate-90" />
                          <span className="sr-only">{item.title}</span>
                        </SidebarMenuAction>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <SidebarMenuSub>
                          {item.children.map((sub) => (
                            <SidebarMenuSubItem key={sub.url}>
                              <SidebarMenuSubButton
                                asChild
                                isActive={location === subItemPath(sub.url) && activeTabParam === subItemTab(sub.url)}
                                data-testid={`link-${sub.title.toLowerCase().replace(/\s+/g, "-")}`}
                              >
                                <Link href={sub.url} onClick={handleNavClick}>
                                  <sub.icon className={`h-4 w-4 ${sub.iconColor || ''}`} />
                                  <span>{sub.title}</span>
                                </Link>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                          ))}
                        </SidebarMenuSub>
                      </CollapsibleContent>
                    </SidebarMenuItem>
                  </Collapsible>
                ) : (
                <SidebarMenuItem key={item.url}>
                  <SidebarMenuButton
                    asChild
                    isActive={location === item.url}
                    tooltip={item.title}
                    data-testid={`link-${item.title.toLowerCase().replace(/\s+/g, "-")}`}
                    className={item.highlight ? "bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 border border-amber-200 dark:border-amber-800" : ""}
                  >
                    <Link href={item.url} onClick={handleNavClick}>
                      <item.icon className={`h-4 w-4 ${item.iconColor || ''}`} />
                      <span className={item.highlight ? "font-medium" : ""}>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                )
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Organization Section - visible to owner and admin only */}
        {canSeeOrgMenu && (
          <Collapsible
            open={orgMenuForcedOpen || orgMenuOpen}
            onOpenChange={setOrgMenuOpen}
            className="group/org-menu"
          >
            <SidebarGroup>
              <SidebarGroupLabel asChild className="px-3 text-xs font-bold uppercase tracking-wide text-muted-foreground">
                <CollapsibleTrigger
                  className="w-full justify-between hover:text-foreground group-data-[collapsible=icon]:invisible"
                  data-testid="button-toggle-organization"
                >
                  Organization
                  <ChevronDown className="h-3.5 w-3.5 shrink-0 transition-transform duration-200 group-data-[state=closed]/org-menu:-rotate-90" />
                </CollapsibleTrigger>
              </SidebarGroupLabel>
              <CollapsibleContent>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {organizationItems.map((item) => (
                      <SidebarMenuItem key={item.url}>
                        <SidebarMenuButton
                          asChild
                          isActive={location === item.url}
                          tooltip={item.title}
                          data-testid={`link-${item.title.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                          <Link href={item.url} onClick={handleNavClick}>
                            <item.icon className={`h-4 w-4 ${item.iconColor || ''}`} />
                            <span>{item.title}</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </CollapsibleContent>
            </SidebarGroup>
          </Collapsible>
        )}

        {/* Telephony Section */}
        <SidebarGroup>
          <SidebarGroupLabel className="px-3 text-xs font-bold uppercase tracking-wide text-muted-foreground">{t('sidebar.telephony')}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {telephonyItems.map((item) => (
                <SidebarMenuItem key={item.url}>
                  <SidebarMenuButton
                    asChild
                    isActive={location === item.url}
                    tooltip={item.title}
                    data-testid={`link-${item.title.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <Link href={item.url} onClick={handleNavClick}>
                      <item.icon className={`h-4 w-4 ${item.iconColor || ''}`} />
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Flow Automation Section - only show if has items */}
        {flowAutomationItems.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel className="px-3 text-xs font-bold uppercase tracking-wide text-muted-foreground">{t('sidebar.flowAutomation')}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {flowAutomationItems.map((item) => (
                  <SidebarMenuItem key={item.url}>
                    <SidebarMenuButton
                      asChild
                      isActive={location === item.url}
                      tooltip={item.title}
                      data-testid={`link-${item.title.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <Link href={item.url} onClick={handleNavClick}>
                        <item.icon className={`h-4 w-4 ${item.iconColor || ''}`} />
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {/* Custom Modules Section - Enterprise tenants only */}
        {customModuleItems.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel className="px-3 text-xs font-bold uppercase tracking-wide text-muted-foreground">
              {tenantBranding.companyName || t('sidebar.customModules', 'Custom Modules')}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {customModuleItems.map((item) => (
                  <SidebarMenuItem key={item.url}>
                    <SidebarMenuButton
                      asChild
                      isActive={location === item.url}
                      tooltip={item.title}
                      data-testid={`link-custom-${item.title.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <Link href={item.url} onClick={handleNavClick}>
                        <item.icon className={`h-4 w-4 ${item.iconColor || ''}`} />
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {/* Support Section */}
        <SidebarGroup>
          <SidebarGroupLabel className="px-3 text-xs font-bold uppercase tracking-wide text-muted-foreground">{t('sidebar.support')}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {supportItems.map((item) => (
                <SidebarMenuItem key={item.url}>
                  <SidebarMenuButton
                    asChild
                    isActive={location === item.url}
                    tooltip={item.title}
                    data-testid={`link-${item.title.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <Link href={item.url} onClick={handleNavClick}>
                      <item.icon className={`h-4 w-4 ${item.iconColor || ''}`} />
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Admin - Show for super admins only */}
        {user.isSuperAdmin && (
          <SidebarGroup>
            <SidebarGroupLabel className="px-3 text-xs font-bold uppercase tracking-wide text-muted-foreground">{t('nav.administration')}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    isActive={location === "/admin"}
                    tooltip={t('nav.adminDashboard')}
                    data-testid="link-admin-dashboard"
                  >
                    <Link href="/admin" onClick={handleNavClick}>
                      <Shield className="h-4 w-4" />
                      <span>{t('nav.adminDashboard')}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    isActive={location === "/admin/seo"}
                    tooltip="SEO Dashboard"
                    data-testid="link-admin-seo"
                  >
                    <Link href="/admin/seo" onClick={handleNavClick}>
                      <Search className="h-4 w-4" />
                      <span>SEO Dashboard</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {/* Credit Status - Silver Shiny Design */}
        <div className="mx-2 my-2 group-data-[collapsible=icon]:hidden">
          <div className="relative overflow-hidden rounded-xl border border-slate-300/60 dark:border-slate-600/60 p-3 bg-gradient-to-br from-slate-100 via-slate-50 to-white dark:from-slate-800 dark:via-slate-700 dark:to-slate-800 shadow-sm">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent dark:via-white/10 -translate-x-full animate-[shimmer_3s_ease-in-out_infinite]" style={{ backgroundSize: '200% 100%' }} />
            <div className="relative">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 rounded-lg bg-gradient-to-br from-slate-200 to-slate-300 dark:from-slate-600 dark:to-slate-700 shadow-inner">
                    <Coins className="h-4 w-4 text-slate-600 dark:text-slate-300" />
                  </div>
                  <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">{t('sidebar.credits')}</span>
                </div>
                {isPro ? (
                  <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold uppercase tracking-wide bg-gradient-to-r from-amber-400/90 to-yellow-500/90 text-amber-900 dark:from-amber-500 dark:to-yellow-600 dark:text-amber-950 shadow-sm border border-amber-500/30 dark:border-yellow-600/30">
                    {t('sidebar.pro')}
                  </span>
                ) : (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="h-6 px-2 text-xs font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-200/60 dark:hover:bg-slate-600/60"
                    onClick={() => setLocation("/app/upgrade")}
                    data-testid="button-upgrade-inline"
                  >
                    {t('sidebar.upgrade')}
                  </Button>
                )}
              </div>
              <div className="mt-1.5 text-xl font-bold bg-gradient-to-r from-slate-700 via-slate-600 to-slate-700 dark:from-slate-100 dark:via-slate-200 dark:to-slate-100 bg-clip-text text-transparent">
                {remainingCredits.toLocaleString()}
              </div>
            </div>
          </div>
        </div>
      </SidebarContent>

      {/* Footer */}
      <SidebarFooter className="px-2 py-1.5 mt-auto border-t border-sidebar-border">
        {/* User Profile Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <div 
              className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover-elevate cursor-pointer"
              data-testid="button-user-menu"
            >
              <Avatar className="h-7 w-7">
                <AvatarFallback className="bg-primary text-primary-foreground text-xs font-medium">
                  {userInitial}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col flex-1 min-w-0">
                <span className="text-xs font-medium text-sidebar-foreground truncate">
                  {userName}
                </span>
                <span className="text-xs text-muted-foreground truncate">{t('sidebar.myWorkspace')}</span>
              </div>
              <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground" />
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-72 p-0">
            {/* Credits Section - Silver Shiny Design */}
            <div className="relative overflow-hidden m-2 rounded-xl border border-slate-300/60 dark:border-slate-600/60 p-4 bg-gradient-to-br from-slate-100 via-slate-50 to-white dark:from-slate-800 dark:via-slate-700 dark:to-slate-800 shadow-sm">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent dark:via-white/10 -translate-x-full animate-[shimmer_3s_ease-in-out_infinite]" style={{ backgroundSize: '200% 100%' }} />
              <div className="relative space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <div className="p-2 rounded-lg bg-gradient-to-br from-slate-200 to-slate-300 dark:from-slate-600 dark:to-slate-700 shadow-inner">
                      <Coins className="h-4 w-4 text-slate-600 dark:text-slate-300" />
                    </div>
                    <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">{t('sidebar.credits')}</span>
                  </div>
                  {isPro ? (
                    <span className="inline-flex items-center rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide bg-gradient-to-r from-amber-400/90 to-yellow-500/90 text-amber-900 dark:from-amber-500 dark:to-yellow-600 dark:text-amber-950 shadow-sm border border-amber-500/30 dark:border-yellow-600/30">
                      {t('sidebar.proMember')}
                    </span>
                  ) : (
                    <Button 
                      variant="default" 
                      size="sm"
                      className="h-7 px-3 text-xs font-semibold"
                      onClick={() => setLocation("/app/upgrade")}
                      data-testid="button-upgrade-dropdown"
                    >
                      {t('sidebar.upgrade')}
                    </Button>
                  )}
                </div>
                <div className="text-2xl font-bold bg-gradient-to-r from-slate-700 via-slate-600 to-slate-700 dark:from-slate-100 dark:via-slate-200 dark:to-slate-100 bg-clip-text text-transparent">
                  {remainingCredits.toLocaleString()} <span className="text-sm font-medium text-slate-500 dark:text-slate-400">credits</span>
                </div>
              </div>
            </div>
            <DropdownMenuSeparator />
            
            {/* Account Settings */}
            <DropdownMenuItem 
              onClick={() => setLocation("/app/settings")}
              className="cursor-pointer"
              data-testid="link-account-settings"
            >
              <Settings className="mr-2 h-4 w-4" />
              <span>{t('nav.accountSettings')}</span>
            </DropdownMenuItem>
            
            <DropdownMenuSeparator />
            
            {/* Log out */}
            <DropdownMenuItem 
              onClick={handleLogout}
              className="cursor-pointer text-destructive focus:text-destructive"
              data-testid="button-logout"
            >
              <LogOut className="mr-2 h-4 w-4" />
              <span>{t('auth.logout')}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
